import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import { FaChevronLeft, FaChevronRight, FaSort, FaSortUp, FaSortDown, FaInbox } from 'react-icons/fa';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const DataTable = ({
  data = [],
  columns = [],
  loading = false,
  serverSide = false,
  totalElements = 0,
  page = 0,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  enableSelection = false,
  onSelectionChange,
  bulkActions = [],
}) => {
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnFilters, setColumnFilters] = React.useState([]);

  const selectionColumn = useMemo(() => ({
    id: 'select',
    header: ({ table }) => (
      <input
        type="checkbox"
        checked={table.getIsAllPageRowsSelected()}
        onChange={table.getToggleAllPageRowsSelectedHandler()}
        className="dt-checkbox"
        aria-label="Select all rows"
      />
    ),
    cell: ({ row }) => (
      <input
        type="checkbox"
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
        className="dt-checkbox"
        aria-label="Select row"
      />
    ),
    size: 44,
  }), []);

  const tableColumns = useMemo(
    () => (enableSelection ? [selectionColumn, ...columns] : columns),
    [enableSelection, columns, selectionColumn]
  );

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: { rowSelection, columnFilters },
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: serverSide,
    pageCount: serverSide ? Math.ceil(totalElements / pageSize) : undefined,
  });

  React.useEffect(() => {
    if (onSelectionChange) {
      const selectedRows = table.getSelectedRowModel().rows.map(r => r.original);
      onSelectionChange(selectedRows);
    }
  }, [rowSelection, onSelectionChange, table]);

  const totalPages = serverSide
    ? Math.ceil(totalElements / pageSize) || 1
    : table.getPageCount() || 1;

  const currentPage = serverSide ? page : table.getState().pagination.pageIndex;

  const getPageButtons = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
    const pages = [];
    if (currentPage <= 3) {
      pages.push(0, 1, 2, 3, 4, '...', totalPages - 1);
    } else if (currentPage >= totalPages - 4) {
      pages.push(0, '...', totalPages - 5, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1);
    } else {
      pages.push(0, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages - 1);
    }
    return pages;
  };

  const handlePageChange = p => {
    if (serverSide) { onPageChange?.(p); }
    else { table.setPageIndex(p); }
  };

  const handlePrev = () => {
    if (currentPage > 0) handlePageChange(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages - 1) handlePageChange(currentPage + 1);
  };

  const handlePageSizeChange = val => {
    if (serverSide) {
      onPageSizeChange?.(val);
      onPageChange?.(0);
    } else {
      table.setPageSize(val);
    }
  };

  const totalCount = serverSide ? totalElements : data.length;
  const startRow = serverSide ? page * pageSize + 1 : currentPage * pageSize + 1;
  const endRow   = serverSide
    ? Math.min((page + 1) * pageSize, totalElements)
    : Math.min((currentPage + 1) * pageSize, data.length);
  const selectedCount = Object.keys(rowSelection).length;
  const filteredCount = serverSide ? totalElements : table.getFilteredRowModel().rows.length;
  const activeFilterCount = columnFilters.filter(filter => String(filter.value || '').trim() !== '').length;
  const sortState = table.getState().sorting || [];

  const getHeaderText = header => {
    const label = header.column.columnDef.header;
    return typeof label === 'string' ? label : header.column.id;
  };

  const canShowColumnFilter = header => {
    const colDef = header.column.columnDef || {};
    return !header.isPlaceholder
      && header.column.getCanFilter()
      && header.column.id !== 'select'
      && colDef.header !== 'Actions'
      && (colDef.accessorKey || colDef.accessorFn);
  };

  if (loading && data.length === 0) {
    return (
      <div className="dt-skeleton-wrap">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="dt-skeleton-row" style={{ animationDelay: `${i * 0.06}s` }} />
        ))}
      </div>
    );
  }

  return (
    <div className="dt-wrapper">
      <div className="dt-summary-tiles" aria-label="Table summary">
        <div className="dt-summary-tile records">
          <span>Records</span>
          <strong>{totalCount}</strong>
        </div>
        <div className="dt-summary-tile visible">
          <span>Visible</span>
          <strong>{filteredCount}</strong>
        </div>
        <div className="dt-summary-tile filters">
          <span>Filters</span>
          <strong>{activeFilterCount}</strong>
        </div>
        {sortState.length > 0 && (
          <div className="dt-summary-tile sorting">
            <span>Sorted</span>
            <strong>{sortState.length}</strong>
          </div>
        )}
        {selectedCount > 0 && (
          <div className="dt-summary-tile selected">
            <span>Selected</span>
            <strong>{selectedCount}</strong>
          </div>
        )}
      </div>

      {/* Bulk actions bar */}
      {selectedCount > 0 && bulkActions.length > 0 && (
        <div className="dt-bulk-bar">
          <span className="dt-bulk-count">{selectedCount} row{selectedCount !== 1 ? 's' : ''} selected</span>
          <div className="dt-bulk-actions">
            {bulkActions.map((action, i) => (
              <button
                key={i}
                className={`dt-bulk-btn ${action.className || ''}`}
                onClick={() => action.onClick(table.getSelectedRowModel().rows.map(r => r.original))}
              >
                {action.icon} {action.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="dt-scroll">
        <table className="dt-table">
          <thead>
            {table.getHeaderGroups().map(hg => (
              <React.Fragment key={hg.id}>
                <tr>
                  {hg.headers.map(header => (
                    <th
                      key={header.id}
                      style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                      className={header.column.getCanSort() ? 'sortable' : ''}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="dt-th-inner">
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <span className="dt-sort-icon">
                            {{ asc: <FaSortUp />, desc: <FaSortDown /> }[header.column.getIsSorted()] || <FaSort />}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
                <tr className="dt-filter-row">
                  {hg.headers.map(header => (
                  <th
                    key={`${header.id}-filter`}
                    style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                    className="dt-filter-cell"
                  >
                    {canShowColumnFilter(header) ? (
                      <input
                        className="dt-column-filter"
                        value={header.column.getFilterValue() || ''}
                        onChange={event => header.column.setFilterValue(event.target.value)}
                        onClick={event => event.stopPropagation()}
                        placeholder={`Filter ${getHeaderText(header)}`}
                      />
                    ) : null}
                  </th>
                  ))}
                </tr>
              </React.Fragment>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={tableColumns.length}>
                  <div className="dt-empty">
                    <FaInbox className="dt-empty-icon" />
                    <div className="dt-empty-title">No records found</div>
                    <div className="dt-empty-sub">Try adjusting your search or filters</div>
                  </div>
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map(row => (
                <tr key={row.id} className={row.getIsSelected() ? 'row-selected' : ''}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer: count + pagination */}
      {totalCount > 0 && (
        <div className="dt-footer">
          <div className="dt-footer-left">
            <span className="dt-count-info">
              Showing <strong>{startRow}–{endRow}</strong> of <strong>{totalCount}</strong> records
            </span>
            <div className="dt-page-size">
              <label>Rows:</label>
              <select
                value={serverSide ? pageSize : table.getState().pagination.pageSize}
                onChange={e => handlePageSizeChange(Number(e.target.value))}
                className="dt-size-select"
              >
                {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="dt-pagination">
            <button
              className="dt-page-btn nav"
              onClick={handlePrev}
              disabled={currentPage === 0}
              aria-label="Previous page"
            >
              <FaChevronLeft />
            </button>

            {getPageButtons().map((p, i) =>
              p === '...'
                ? <span key={`ellipsis-${i}`} className="dt-ellipsis">…</span>
                : (
                  <button
                    key={p}
                    className={`dt-page-btn${p === currentPage ? ' active' : ''}`}
                    onClick={() => handlePageChange(p)}
                    aria-label={`Page ${p + 1}`}
                    aria-current={p === currentPage ? 'page' : undefined}
                  >
                    {p + 1}
                  </button>
                )
            )}

            <button
              className="dt-page-btn nav"
              onClick={handleNext}
              disabled={currentPage >= totalPages - 1}
              aria-label="Next page"
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
      )}

      <style>{`
        .dt-wrapper {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          overflow: hidden;
          box-sizing: border-box;
        }

        .dt-summary-tiles {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
          gap: 8px;
          margin: 12px 0 10px;
        }
        .dt-summary-tile {
          background: #f8fafc;
          border: 1px solid transparent;
          border-radius: 7px;
          padding: 8px 10px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          min-height: 36px;
        }
        .dt-summary-tile.records {
          background: #ecfdf5;
          border-color: #bbf7d0;
        }
        .dt-summary-tile.visible {
          background: #eff6ff;
          border-color: #bfdbfe;
        }
        .dt-summary-tile.filters {
          background: #fff7ed;
          border-color: #fed7aa;
        }
        .dt-summary-tile.sorting {
          background: #f5f3ff;
          border-color: #ddd6fe;
        }
        .dt-summary-tile.selected {
          background: #fef2f2;
          border-color: #fecaca;
        }
        .dt-summary-tile span {
          color: #64748b;
          font-size: 0.68rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }
        .dt-summary-tile strong {
          color: #0f172a;
          font-size: 0.86rem;
          font-weight: 800;
          white-space: nowrap;
        }

        /* Skeleton */
        .dt-skeleton-wrap { display: flex; flex-direction: column; gap: 7px; padding: 0 0 10px; }
        .dt-skeleton-row {
          height: 36px; border-radius: 7px;
          background: linear-gradient(90deg, #f0f4f8 25%, #e8ecf0 50%, #f0f4f8 75%);
          background-size: 200% 100%;
          animation: dtShimmer 1.4s infinite;
        }
        @keyframes dtShimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        /* Table */
        .dt-scroll {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          overflow-x: auto;
          overscroll-behavior-x: contain;
        }
        .dt-table {
          width: 100%; border-collapse: collapse;
          font-family: 'Poppins', sans-serif;
        }
        .dt-table thead tr { background: #f8fafc; }
        .dt-table th {
          padding: 8px 10px;
          text-align: left;
          font-size: 0.66rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          color: #64748b;
          border-bottom: 1px solid #e2e8f0;
          white-space: nowrap;
          user-select: none;
        }
        .dt-table th.sortable { cursor: pointer; }
        .dt-table th.sortable:hover { color: #1e293b; background: #f0f4f8; }
        .dt-th-inner {
          display: flex; align-items: center; gap: 6px;
        }
        .dt-sort-icon {
          color: #94a3b8; font-size: 0.7rem; flex-shrink: 0;
        }
        .dt-filter-row th {
          background: #ffffff;
          padding: 6px 8px;
          border-bottom: 1px solid #e5e7eb;
        }
        .dt-filter-cell {
          cursor: default !important;
        }
        .dt-column-filter {
          width: 100%;
          min-width: 96px;
          height: 28px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 4px 7px;
          color: #334155;
          background: #f8fafc;
          font-size: 0.72rem;
          font-family: 'Poppins', sans-serif;
          outline: none;
        }
        .dt-column-filter:focus {
          border-color: #10ac84;
          background: #ffffff;
          box-shadow: 0 0 0 2px rgba(16, 172, 132, 0.11);
        }
        .dt-table td {
          padding: 8px 10px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 0.78rem;
          color: #374151;
          vertical-align: middle;
        }
        .dt-table tbody tr {
          transition: background 0.12s;
        }
        .dt-table tbody tr:hover { background: #fafbfe; }
        .dt-table tbody tr.row-selected { background: rgba(16, 172, 132, 0.05); }
        .dt-table tbody tr:last-child td { border-bottom: none; }

        /* Checkbox */
        .dt-checkbox {
          width: 16px; height: 16px; cursor: pointer;
          accent-color: var(--eduss-primary, #10ac84);
        }

        /* Empty state */
        .dt-empty {
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          padding: 38px 18px; text-align: center;
        }
        .dt-empty-icon {
          font-size: 2rem; color: #d1d5db; margin-bottom: 10px;
        }
        .dt-empty-title { font-size: 0.86rem; font-weight: 600; color: #6b7280; }
        .dt-empty-sub { font-size: 0.75rem; color: #9ca3af; margin-top: 3px; }

        /* Bulk actions */
        .dt-bulk-bar {
          display: flex; align-items: center; justify-content: space-between;
          padding: 8px 10px; margin-bottom: 8px;
          background: rgba(16, 172, 132, 0.06);
          border: 1px solid rgba(16, 172, 132, 0.15);
          border-radius: 10px;
          animation: dtSlideDown 0.2s ease;
        }
        @keyframes dtSlideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .dt-bulk-count { font-size: 0.76rem; font-weight: 600; color: #10ac84; }
        .dt-bulk-actions { display: flex; gap: 6px; }
        .dt-bulk-btn {
          padding: 5px 10px; border-radius: 6px; font-size: 0.74rem;
          font-weight: 600; cursor: pointer; border: 1px solid transparent;
          display: flex; align-items: center; gap: 6px;
          font-family: 'Poppins', sans-serif;
        }
        .dt-bulk-btn.delete {
          background: rgba(231, 76, 60, 0.08); color: #e74c3c;
          border-color: rgba(231, 76, 60, 0.15);
        }
        .dt-bulk-btn.delete:hover { background: #e74c3c; color: #fff; }

        /* Footer */
        .dt-footer {
          display: flex; align-items: center; justify-content: space-between;
          padding: 9px 2px 0; flex-wrap: wrap; gap: 8px;
          border-top: 1px solid #f1f5f9; margin-top: 4px;
        }
        .dt-footer-left { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        .dt-count-info { font-size: 0.74rem; color: #64748b; }
        .dt-count-info strong { color: #1e293b; }
        .dt-page-size {
          display: flex; align-items: center; gap: 8px;
          font-size: 0.74rem; color: #64748b;
        }
        .dt-size-select {
          padding: 3px 7px; border: 1px solid #e2e8f0; border-radius: 6px;
          font-size: 0.74rem; outline: none; cursor: pointer;
          background: #fff; color: #374151;
          font-family: 'Poppins', sans-serif;
        }
        .dt-size-select:focus { border-color: #10ac84; }

        /* Pagination */
        .dt-pagination { display: flex; align-items: center; gap: 4px; }
        .dt-page-btn {
          min-width: 28px; height: 28px; border-radius: 6px;
          border: 1px solid #e2e8f0; background: #fff;
          color: #374151; cursor: pointer; font-size: 0.74rem;
          font-weight: 500; font-family: 'Poppins', sans-serif;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.15s; padding: 0 6px;
        }
        .dt-page-btn:hover:not(:disabled):not(.active) {
          border-color: #10ac84; color: #10ac84;
        }
        .dt-page-btn.active {
          background: #10ac84; border-color: #10ac84;
          color: #fff; font-weight: 700;
        }
        .dt-page-btn:disabled {
          opacity: 0.35; cursor: not-allowed;
        }
        .dt-page-btn.nav { color: #64748b; }
        .dt-ellipsis {
          width: 28px; height: 28px;
          display: flex; align-items: center; justify-content: center;
          font-size: 0.75rem; color: #94a3b8;
        }
      `}</style>
    </div>
  );
};

DataTable.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array,
  loading: PropTypes.bool,
  serverSide: PropTypes.bool,
  totalElements: PropTypes.number,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  enableSelection: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  bulkActions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    icon: PropTypes.node,
    className: PropTypes.string,
    onClick: PropTypes.func,
  })),
};

export default DataTable;
